import { useEffect, useRef, useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import io from 'socket.io-client';

const useWebSocket = (url = '', options = {}) => {
    const [isConnected, setIsConnected] = useState(false);
    const [lastMessage, setLastMessage] = useState(null);
    const [error, setError] = useState(null);
    const socketRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const maxReconnectAttempts = 5;

    const {
        autoConnect = true,
        reconnectDelay = 3000,
        onConnect,
        onDisconnect,
        onError,
        onMessage,
        enableLogging = false
    } = options;

    const log = useCallback((message, data = null) => {
        if (enableLogging) {
            console.log(`[WebSocket] ${message}`, data);
        }
    }, [enableLogging]);

    const connect = useCallback(() => {
        if (socketRef.current?.connected) {
            log('Already connected');
            return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
            setError('No authentication token found');
            return;
        }

        log('Connecting to WebSocket...');
        
        const socketUrl = url || window.location.origin;
        socketRef.current = io(socketUrl, {
            transports: ['websocket', 'polling'], // Add polling fallback
            upgrade: true,
            rememberUpgrade: true,
            timeout: 20000, // Increase timeout
            reconnection: true,
            reconnectionAttempts: 3,
            reconnectionDelay: 2000
        });

        const socket = socketRef.current;

        socket.on('connect', () => {
            log('Connected to server');
            setIsConnected(true);
            setError(null);
            setReconnectAttempts(0);

            // Authenticate with server
            socket.emit('authenticate', { token });

            onConnect?.();
        });

        socket.on('authenticated', (data) => {
            log('Authenticated successfully', data);
            toast.success('🔗 Connected to live updates');
        });

        socket.on('auth_error', (data) => {
            log('Authentication error', data);
            setError(`Authentication failed: ${data.message}`);
            toast.error(`Authentication failed: ${data.message}`);
        });

        socket.on('disconnect', (reason) => {
            log('Disconnected from server', reason);
            setIsConnected(false);
            onDisconnect?.(reason);

            // Only show toast if not a planned disconnect
            if (reason !== 'io client disconnect') {
                toast.error('🔗 Lost connection to live updates');
            }

            // Attempt to reconnect if not a client-initiated disconnect
            if (reason !== 'io client disconnect' && reconnectAttempts < maxReconnectAttempts) {
                attemptReconnect();
            }
        });

        socket.on('connect_error', (err) => {
            log('Connection error', err);
            setError(`Connection error: ${err.message}`);
            onError?.(err);

            if (reconnectAttempts < maxReconnectAttempts) {
                attemptReconnect();
            }
        });

        socket.on('error', (err) => {
            log('Socket error', err);
            setError(`Socket error: ${err.message}`);
            onError?.(err);
        });

        // Handle subscription responses
        socket.on('subscription_success', (data) => {
            log('Subscribed successfully', data);
            toast.success(`📺 Subscribed to ${data.channel}`);
        });

        socket.on('subscription_error', (data) => {
            log('Subscription error', data);
            toast.error(`Failed to subscribe: ${data.message}`);
        });

        // Handle all incoming messages
        socket.onAny((event, ...args) => {
            const messageData = {
                event,
                data: args[0],
                timestamp: new Date().toISOString()
            };
            
            setLastMessage(messageData);
            onMessage?.(messageData);
            log(`Received: ${event}`, args[0]);
        });

    }, [url, onConnect, onDisconnect, onError, onMessage, reconnectAttempts, log]);

    const attemptReconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }

        setReconnectAttempts(prev => {
            const newAttempts = prev + 1;
            
            if (newAttempts <= maxReconnectAttempts) {
                log(`Attempting to reconnect (${newAttempts}/${maxReconnectAttempts})...`);
                
                reconnectTimeoutRef.current = setTimeout(() => {
                    connect();
                }, reconnectDelay * Math.pow(1.5, newAttempts - 1)); // Exponential backoff
            } else {
                log('Max reconnection attempts reached');
                toast.error('🚫 Unable to connect to live updates. Please refresh the page.');
            }
            
            return newAttempts;
        });
    }, [connect, reconnectDelay, log]);

    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }

        if (socketRef.current) {
            log('Disconnecting...');
            socketRef.current.disconnect();
            socketRef.current = null;
        }

        setIsConnected(false);
        setError(null);
        setReconnectAttempts(0);
    }, [log]);

    const emit = useCallback((event, data) => {
        if (socketRef.current?.connected) {
            log(`Emitting: ${event}`, data);
            socketRef.current.emit(event, data);
            return true;
        } else {
            log(`Cannot emit ${event}: not connected`);
            return false;
        }
    }, [log]);

    const subscribe = useCallback((channel) => {
        return emit(`subscribe_${channel}`);
    }, [emit]);

    const unsubscribe = useCallback((channel) => {
        return emit(`unsubscribe_${channel}`);
    }, [emit]);

    // Auto-connect on mount
    useEffect(() => {
        if (autoConnect) {
            connect();
        }

        return () => {
            disconnect();
        };
    }, [autoConnect, connect, disconnect]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, []);

    return {
        isConnected,
        lastMessage,
        error,
        connect,
        disconnect,
        emit,
        subscribe,
        unsubscribe,
        reconnectAttempts,
        maxReconnectAttempts
    };
};

export default useWebSocket;
